import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DailyBreakdownTable } from './components/DailyBreakdownTable';
import { CashFlowWaterfall } from './components/CashFlowWaterfall';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { formatCurrency, getCurrencySymbol } from '../charts/chartTheme';
import {
    TrendingUp,
    TrendingDown,
    DollarSign,
    Target
} from 'lucide-react';
import { format, parseISO, eachDayOfInterval, startOfMonth, endOfMonth, startOfDay, startOfWeek, endOfWeek, getWeek } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { BoardBottomDrawer } from '../BoardBottomDrawer';
import { AIVerdictModule } from '../AIVerdictModule';
import { useBoardStore } from '../../../../stores/boardStore';

const KPICard = React.memo(({ title, value, unit = '', icon: Icon, color, isCurrency = true, precision = 2, formatCurrency, currencySymbol }: any) => (
    <div className="bg-white dark:bg-neutral-900 rounded-xl p-3 border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md transition-all duration-300 animate-in fade-in zoom-in-95 duration-200 flex flex-col justify-center h-full">
        <div className="flex items-center gap-1.5 mb-1.5">
            <div className={`p-1 rounded-lg ${color} bg-opacity-10 dark:bg-opacity-20`}>
                <Icon className={`w-3 h-3 ${color.replace('bg-', 'text-')}`} />
            </div>
            <h3 className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider truncate mr-2">{title}</h3>
        </div>
        <div className="text-base md:text-lg font-black text-neutral-900 dark:text-neutral-100 truncate">
            {isCurrency ? formatCurrency(value, currencySymbol) :
                (typeof value === 'string' ? value : `${(value || 0).toFixed(precision)}${unit}`)}
        </div>
    </div>
));

interface CashFlowPageProps {
    isEditMode?: boolean;
}

/**
 * CashFlowPage - Specialized view for monitoring money movement.
 * This page is independent of the standard board grid.
 */
export const CashFlowPage: React.FC<CashFlowPageProps> = () => {
    const { t, i18n: i18nInstance } = useTranslation("common");
    const { finance, currencySettings, dateRange, channels, entities } = useBoardChartData();
    const boardId = useBoardStore(state => state.boardId);
    const currencySymbol = getCurrencySymbol(currencySettings?.default);
    
    const [localViewMode, setLocalViewMode] = React.useState<'weeks' | 'months' | 'all'>('months');
    const [selectedPeriodIndex, setSelectedPeriodIndex] = React.useState(0);
    const [isAiMobileExpanded, setIsAiMobileExpanded] = React.useState(false);
    const currentLocale = i18nInstance.language === 'fr' ? fr : enUS;

    const periods = useMemo(() => {
        const start = dateRange.startDate ? startOfDay(dateRange.startDate) : startOfMonth(new Date());
        const end = dateRange.endDate ? startOfDay(dateRange.endDate) : endOfMonth(new Date());
        const intervalDays = eachDayOfInterval({ start, end });

        const generatedPeriods: { title: string, days: Date[] }[] = [];
        if (localViewMode === 'weeks') {
            const weeksSet = new Set<string>();
            intervalDays.forEach(day => {
                const wStart = startOfWeek(day, { weekStartsOn: 1 });
                const weekStr = format(wStart, 'yyyy-MM-dd');
                if (!weeksSet.has(weekStr)) {
                    weeksSet.add(weekStr);
                    const weekNum = getWeek(day, { weekStartsOn: 1 });
                    const periodDays = eachDayOfInterval({
                        start: wStart,
                        end: endOfWeek(day, { weekStartsOn: 1 })
                    }).filter(d => d >= start && d <= end);
                    generatedPeriods.push({ title: t('charts.components.week_number', { number: weekNum, defaultValue: `Week ${weekNum}` }), days: periodDays });
                }
            });
        } else if (localViewMode === 'months') {
            const monthsSet = new Set<string>();
            intervalDays.forEach(day => {
                const mStart = startOfMonth(day);
                const monthStr = format(mStart, 'yyyy-MM');
                if (!monthsSet.has(monthStr)) {
                    monthsSet.add(monthStr);
                    const periodDays = eachDayOfInterval({
                        start: mStart,
                        end: endOfMonth(day)
                    }).filter(d => d >= start && d <= end);
                    generatedPeriods.push({ title: format(day, 'MMMM yyyy', { locale: currentLocale }), days: periodDays });
                }
            });
        }
        return generatedPeriods;
    }, [dateRange, localViewMode, currentLocale, t]);

    const activePeriod = useMemo(() => {
        if (localViewMode === 'all') {
            const start = dateRange.startDate ? startOfDay(dateRange.startDate) : startOfMonth(new Date());
            const end = dateRange.endDate ? startOfDay(dateRange.endDate) : endOfMonth(new Date());
            return { title: t('charts.components.all', 'All'), days: eachDayOfInterval({ start, end }) };
        }
        return periods[selectedPeriodIndex] || { title: '', days: [] };
    }, [localViewMode, periods, selectedPeriodIndex, dateRange, t]);

    const kpis = useMemo(() => {
        let totalSales = 0;
        let totalAds = 0;
        let totalOpEx = 0;
        let netProfit = 0;

        const intervalDays = activePeriod.days;
        const allDaysObj: Record<string, boolean> = {};
        intervalDays.forEach(d => allDaysObj[format(d, 'yyyy-MM-dd')] = true);

        const txs = finance?.transactions || [];

        const channelMap: Record<string, any> = {};
        if (channels) channels.forEach((c: any) => { channelMap[c.id] = c; });

        const getChannelCategory = (channel: any): 'ads' | 'sales' => {
            const cat = channel?.channelTypeConfig?.category?.toLowerCase() || '';
            if (cat === 'advertising' || cat === 'marketing' || cat === 'ads') return 'ads';
            return 'sales';
        };

        const daysCount = intervalDays.length || 1;

        txs.forEach((tx: any) => {
            if (tx.isActive === false || !tx.date || !tx.amount) return;
            const parsedDate = parseISO(tx.date);
            if (isNaN(parsedDate.getTime())) return;
            const dateStr = format(parsedDate, 'yyyy-MM-dd');
            if (!allDaysObj[dateStr]) return;

            const val = tx.type === 'EXPENSE' ? -Math.abs(tx.amount) : Math.abs(tx.amount);

            const entityId = tx.entityId ? String(tx.entityId) : '';
            if (entityId.startsWith('journal-') || tx.source === 'spec' || entityId.startsWith('spec-') || tx.category === 'Calculated') {
                if (val < 0) totalOpEx += Math.abs(val);
                netProfit += val;
                return;
            }

            const txChannelId = tx.channelId;
            if (txChannelId) {
                const channel = channelMap[txChannelId];
                if (channel) {
                    const category = getChannelCategory(channel);
                    if (category === 'ads') {
                        totalAds += Math.abs(val);
                        netProfit += val;
                    } else if (category === 'sales') {
                        totalSales += val;
                        netProfit += val;
                    }
                }
            }
        });

        const mer = totalAds > 0 ? totalSales / totalAds : 0;
        const avgDailyNet = netProfit / daysCount;

        return {
            totalSales,
            totalAds,
            totalOpEx,
            netProfit,
            mer,
            avgDailyNet
        };
    }, [finance, activePeriod, channels, entities]);

    return (
        <div className="h-full flex flex-col bg-white dark:bg-neutral-900 overflow-hidden relative">
            <div className="flex-1 overflow-hidden relative flex flex-col">
                <div 
                    className="flex-1 overflow-y-auto fade-scrollbar px-2 lg:px-4"
                    onScroll={(e) => {
                        const container = e.currentTarget;
                        container.classList.add('scrolling');
                        const existingTimeout = (container as any).dataset.scrollTimeout;
                        if (existingTimeout) clearTimeout(parseInt(existingTimeout));
                        const timeoutId = setTimeout(() => {
                            container.classList.remove('scrolling');
                        }, 1000);
                        (container as any).dataset.scrollTimeout = timeoutId.toString();
                    }}
                >
                    <div className="pt-2 lg:pt-4 space-y-4 lg:space-y-6 pb-20">
                        {/* KPIs */}
                        {/* Top Interaction Area */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                            {/* KPIs (Span 8) */}
                            <div className="lg:col-span-8 flex flex-col gap-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <KPICard
                                        title={t('pages.cashflow.kpis.netProfit', 'Net Cash Flow')}
                                        value={kpis.netProfit}
                                        icon={TrendingUp}
                                        color={kpis.netProfit >= 0 ? "bg-emerald-500" : "bg-red-500"}
                                        formatCurrency={formatCurrency}
                                        currencySymbol={currencySymbol}
                                    />
                                    <KPICard
                                        title={t('pages.cashflow.kpis.mer', 'MER (Blended ROAS)')}
                                        value={kpis.mer}
                                        unit="x"
                                        isCurrency={false}
                                        icon={Target}
                                        color="bg-indigo-500"
                                        formatCurrency={formatCurrency}
                                        currencySymbol={currencySymbol}
                                    />
                                    <KPICard
                                        title={t('pages.cashflow.kpis.avgDaily', 'Avg Daily Net')}
                                        value={kpis.avgDailyNet}
                                        icon={DollarSign}
                                        color={kpis.avgDailyNet >= 0 ? "bg-emerald-500" : "bg-red-500"}
                                        formatCurrency={formatCurrency}
                                        currencySymbol={currencySymbol}
                                    />
                                    <KPICard
                                        title={t('pages.cashflow.kpis.opex', 'Total OpEx')}
                                        value={kpis.totalOpEx}
                                        icon={TrendingDown}
                                        color="bg-amber-500"
                                        formatCurrency={formatCurrency}
                                        currencySymbol={currencySymbol}
                                    />
                                </div>
                                <div className="flex-1 bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm overflow-hidden relative">
                                    <DailyBreakdownTable isEditMode={false} forceViewMode={localViewMode} forcePeriodIndex={selectedPeriodIndex} hideFooter={true} />
                                </div>
                            </div>

                            {/* Waterfall & AI (Span 4) */}
                            <div className="lg:col-span-4 flex flex-col gap-6">
                                {/* Waterfall Area */}
                                <div className="h-auto flex flex-col bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm p-4 relative group hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4 shrink-0">
                                        <h3 className="text-[11px] font-bold text-neutral-800 dark:text-neutral-200 uppercase tracking-widest">{t('charts.components.cashflowWaterfall', 'Cash Flow Breakdown')}</h3>
                                        <TrendingUp className="w-3.5 h-3.5 text-indigo-500" />
                                    </div>
                                    <div className="flex-1 overflow-hidden relative">
                                        <CashFlowWaterfall isEditMode={false} days={activePeriod.days} />
                                    </div>
                                </div>

                                {/* AI Insight Card */}
                                <div className="hidden lg:flex flex-col flex-1">
                                    <AIVerdictModule 
                                        pageId="cashflow" 
                                        boardId={boardId || ''} 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile AI Drawer */}
                <BoardBottomDrawer
                    isOpen={isAiMobileExpanded}
                    onClose={() => setIsAiMobileExpanded(false)}
                    onOpen={() => setIsAiMobileExpanded(true)}
                    height="100%"
                    initialHeight="84px"
                    showTints={true}
                    className="lg:hidden"
                >
                    <div className="cursor-pointer" onClick={() => !isAiMobileExpanded && setIsAiMobileExpanded(true)}>
                        <div className="text-[10px] font-bold text-accent-primary uppercase tracking-widest mb-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-accent-primary animate-pulse" />
                                {t('pages.performance.aiInsight', 'AI Insight')}
                            </div>
                        </div>
                        <h4 className={`font-bold text-text-primary mb-2 transition-all ${isAiMobileExpanded ? 'text-lg' : 'text-sm'}`}>
                            {t('pages.cashflow.verdict.title', 'Cash Flow Verdict')}
                        </h4>

                        {isAiMobileExpanded && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2 }}
                                className="pt-2"
                            >
                                <AIVerdictModule 
                                    pageId="cashflow" 
                                    boardId={boardId || ''} 
                                />
                            </motion.div>
                        )}
                    </div>
                </BoardBottomDrawer>
            </div>

            <div className="px-4 py-2 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 flex items-center justify-between shrink-0 z-20">
                 <div className="flex-1 overflow-x-auto scrollbar-hide flex items-center gap-2 mr-6">
                    {periods.map((p, idx) => (
                        <button
                            key={idx}
                            onClick={() => setSelectedPeriodIndex(idx)}
                            className={`whitespace-nowrap px-4 py-1.5 text-xs font-medium rounded-full transition-all flex-shrink-0 border ${selectedPeriodIndex === idx
                                ? 'bg-neutral-800 text-white border-neutral-800 dark:bg-white dark:text-neutral-900 dark:border-white shadow-sm'
                                : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'
                                }`}
                        >
                            {p.title}
                        </button>
                    ))}
                 </div>

                  <div className="flex bg-neutral-100 dark:bg-neutral-800 p-0.5 rounded-lg border border-neutral-200 dark:border-neutral-700 shrink-0">
                    <button
                        onClick={() => { setLocalViewMode('all'); setSelectedPeriodIndex(0); }}
                        className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${localViewMode === 'all' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
                    >
                        {t('charts.components.all', 'All')}
                    </button>
                    <button
                        onClick={() => { setLocalViewMode('weeks'); setSelectedPeriodIndex(0); }}
                        className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${localViewMode === 'weeks' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
                    >
                        {t('charts.components.weeks')}
                    </button>
                    <button
                        onClick={() => { setLocalViewMode('months'); setSelectedPeriodIndex(0); }}
                        className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${localViewMode === 'months' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
                    >
                        {t('charts.components.months')}
                    </button>
                </div>
            </div>
        </div>
    );
};
